import { Redirect } from "@quortex/front-common"
import { type DefaultGenerics, Navigate, Outlet, type Route } from "@tanstack/react-location"
import { lazy } from "react"
import OttEndpointList from "@/App/play/App/Main/Pool/endpoints/ottEndpoints/OttEndpointList"
import FillersTab from "@/App/play/App/Main/Pool/inputs/InputView/VirtualInputTabs/FillersTab"
import ContentSteeringServerForm from "@/App/play/App/Main/Settings/ContentSteering/ContentSteeringServerForm"

// Misc
const App = lazy(() => import("./App"))

// Landings
const NotFound = lazy(() =>
  import("@quortex/front-common").then(module => ({ default: module.NotFound }))
)

// Main
const Main = lazy(() => import("./App/Main"))

// Pool
const Pool = lazy(() => import("./App/Main/Pool"))
const Status = lazy(() => import("./App/Main/Pool/status/Status"))
const Analytics = lazy(() => import("./App/Main/Pool/analytics/Analytics"))
const AssetLibrary = lazy(() => import("./App/Main/Pool/assets/AssetLibrary"))
const PoolSettingList = lazy(() => import("./App/Main/Pool/settings/PoolSettings"))
const EditCdn = lazy(() => import("./App/Main/Pool/settings/PoolSettings/cdns/EditCdn"))
const PoolLocationForm = lazy(() => import("./App/Main/Pool/settings/PoolSettings/PoolLocation"))
const AddPoolApiKey = lazy(() => import("./App/Main/Pool/settings/PoolSettings/apiKeys/AddApiKey"))
const EditPoolApiKey = lazy(
  () => import("./App/Main/Pool/settings/PoolSettings/apiKeys/EditApiKey")
)
const EventLog = lazy(() => import("./App/Main/Pool/eventLogList/EventLog"))

const AddProcessing = lazy(() => import("./components/features/processing/AddProcessing"))
const AddProcessingDialogStep1 = lazy(
  () => import("./components/features/processing/AddProcessingDialogStep1")
)
const AddProcessingDialogStep2 = lazy(
  () => import("./components/features/processing/AddProcessingDialogStep2")
)
const EditProcessing = lazy(() => import("./components/features/processing/EditProcessing"))
const EditProcessingTab = lazy(
  () => import("@/App/play/components/features/processing/EditProcessingTab")
)
const ProcessingList = lazy(() => import("./App/Main/Pool/processing/ProcessingList"))

const AddPackaging = lazy(() => import("./components/features/packaging/AddPackaging"))
const AddPackagingDialogStep1 = lazy(
  () => import("./components/features/packaging/AddPackagingDialogStep1")
)
const AddPackagingDialogStep2 = lazy(
  () => import("./components/features/packaging/AddPackagingDialogStep2")
)
const EditPackaging = lazy(() => import("./components/features/packaging/EditPackaging"))
const EditPackagingTab = lazy(
  () => import("@/App/play/components/features/packaging/EditPackagingTab")
)
const PackagingList = lazy(() => import("./components/features/packaging/PackagingList"))

const EndpointsLayout = lazy(() => import("./App/Main/Pool/endpoints/EndpointsLayout"))
const OttEndpointView = lazy(() => import("./App/Main/Pool/endpoints/ottEndpoints/OttEndpointView"))
const PushEndpoints = lazy(() => import("./App/Main/Pool/endpoints/pushEndpoints/PushEndpoints"))
const ViewPushEndpoint = lazy(
  () => import("./App/Main/Pool/endpoints/pushEndpoints/ViewPushEndpoint")
)
const L2vEndpointList = lazy(() => import("./App/Main/Pool/endpoints/l2VEndpoints/L2VEndpointList"))

const Programs = lazy(() => import("./App/Main/Pool/catchup/Programs"))
const AddProgram = lazy(() => import("./App/Main/Pool/catchup/Programs/AddProgram"))
const EditProgram = lazy(() => import("./App/Main/Pool/catchup/Programs/EditProgram"))
const AddVodAsset = lazy(() => import("./App/Main/Pool/catchup/VodAssets/AddVodAsset"))
const AddStaticEncryptionConfiguration = lazy(
  () =>
    import(
      "./components/features/encryption_configurations/static/AddStaticEncryptionConfiguration"
    )
)
const EditStaticEncryptionConfiguration = lazy(
  () =>
    import(
      "./components/features/encryption_configurations/static/EditStaticEncryptionConfiguration"
    )
)

//Live2VOD
const Live2Vod = lazy(() => import("./App/Main/Pool/Live2Vod"))
const RecorderView = lazy(() => import("./App/Main/Pool/Live2Vod/recorder/RecorderView"))
const RecorderOverviewTab = lazy(
  () => import("./App/Main/Pool/Live2Vod/recorder/RecorderView/RecorderTabs/OverviewTab")
)
const RecorderAssetsTab = lazy(
  () => import("./App/Main/Pool/Live2Vod/recorder/RecorderView/RecorderTabs/AssetsTab")
)
const RecorderRecordingsTab = lazy(
  () => import("./App/Main/Pool/Live2Vod/recorder/RecorderView/RecorderTabs/RecordingsTab")
)
const RecorderList = lazy(() => import("./App/Main/Pool/Live2Vod/recorder/RecorderList"))
const AssetCollectionList = lazy(
  () => import("./App/Main/Pool/Live2Vod/assetCollections/AssetCollectionList")
)
const AssetCollectionListView = lazy(
  () =>
    import(
      "./App/Main/Pool/Live2Vod/assetCollections/AssetCollectionList/tabs/AssetCollectionListView"
    )
)
const TrashList = lazy(
  () => import("./App/Main/Pool/Live2Vod/assetCollections/AssetCollectionList/tabs/TrashList")
)
const AssetCollectionView = lazy(
  () => import("./App/Main/Pool/Live2Vod/assetCollections/AssetCollectionView")
)
const AssetCollectionAssetList = lazy(
  () => import("./App/Main/Pool/Live2Vod/assetCollections/AssetCollectionView/tabs/AssetList")
)
const AssetCollectionEndpointList = lazy(
  () => import("./App/Main/Pool/Live2Vod/assetCollections/AssetCollectionView/tabs/L2VEndpointList")
)
const AssetCollectionEndpointView = lazy(
  () => import("./App/Main/Pool/Live2Vod/components/l2vEndpoint/L2VEndpointView")
)

// Wizard
const Wizard = lazy(() => import("./App/Wizard"))
const WizardCloudSettings = lazy(() => import("./App/Wizard/cloudsettings/CloudSettings"))
const WizardDone = lazy(() => import("./App/Wizard/done/Done"))

// Settings
const Settings = lazy(() => import("./App/Main/Settings/"))
const DRMProviderList = lazy(() => import("./App/Main/Settings/DRMProviders"))
const EditDRMProvider = lazy(() => import("./App/Main/Settings/DRMProviders/EditDRMProvider"))
const AdminSettings = lazy(() => import("./App/Main/Settings/AdminSettings"))

// Input
const AddInput = lazy(() => import("./components/features/inputs/AddInput"))
const InputList = lazy(() => import("./App/Main/Pool/inputs/InputList"))
const InputView = lazy(() => import("./App/Main/Pool/inputs/InputView"))
const InputOverviewTab = lazy(
  () => import("./App/Main/Pool/inputs/InputView/InputTabs/OverviewTab")
)
const InputLive2VodTab = lazy(
  () => import("./App/Main/Pool/inputs/InputView/InputTabs/Live2VodTab")
)
const InputConfigurationTab = lazy(
  () => import("./App/Main/Pool/inputs/InputView/InputTabs/ConfigurationTab")
)
const InputFallbackTab = lazy(
  () => import("./App/Main/Pool/inputs/InputView/InputTabs/FallbackTab")
)
const InputSlateTab = lazy(() => import("./App/Main/Pool/inputs/InputView/InputTabs/SlateTab"))
const InputLogoTab = lazy(() => import("./App/Main/Pool/inputs/InputView/InputTabs/LogoTab"))
const InputStreamEdit = lazy(
  () => import("./App/Main/Pool/inputs/InputView/InputTabs/ConfigurationTab/InputStreamEdit")
)
const ProgramsTab = lazy(
  () => import("./App/Main/Pool/inputs/InputView/VirtualInputTabs/ProgramsTab")
)
const VirtualConfigurationTab = lazy(
  () => import("./App/Main/Pool/inputs/InputView/VirtualInputTabs/VirtualConfigurationTab")
)

const getAddPackagingRoute = (isWizard: boolean) => ({
  path: "packaging/new",
  element: <AddPackaging isWizard={isWizard} />,
  children: [
    {
      path: "step1",
      children: [
        {
          path: "baseSettings",
          element: <AddPackagingDialogStep1 isWizard={isWizard} tab="baseSettings" />
        },
        {
          path: "encryption",
          children: [
            {
              path: "/",
              element: <AddPackagingDialogStep1 isWizard={isWizard} tab="encryption" />
            },
            {
              path: "static",
              children: [
                {
                  path: "new",
                  element: <AddStaticEncryptionConfiguration />
                },
                {
                  path: ":encryptionUuid",
                  element: <EditStaticEncryptionConfiguration />
                }
              ]
            }
          ]
        },
        {
          path: "scte35",
          element: <AddPackagingDialogStep1 isWizard={isWizard} tab="scte35" />
        },
        {
          path: "advanced",
          element: <AddPackagingDialogStep1 isWizard={isWizard} tab="advanced" />
        }
      ]
    },
    {
      path: "step2",
      element: <AddPackagingDialogStep2 isWizard={isWizard} />
    }
  ]
})

const getEditPackagingRoute = () => ({
  path: "packaging/:targetUuid",
  element: <EditPackaging />,
  children: [
    {
      path: "baseSettings",
      element: <EditPackagingTab tab="baseSettings" />
    },
    {
      path: "encryption",
      children: [
        {
          path: "/",
          element: <EditPackagingTab tab="encryption" />
        },
        {
          path: "static",
          children: [
            {
              path: "new",
              element: <AddStaticEncryptionConfiguration />
            },
            {
              path: ":encryptionUuid",
              element: <EditStaticEncryptionConfiguration />
            }
          ]
        }
      ]
    },
    {
      path: "scte35",
      element: <EditPackagingTab tab="scte35" />
    },
    {
      path: "advanced",
      element: <EditPackagingTab tab="advanced" />
    }
  ]
})

const getAddProcessingRoute = (isWizard: boolean) => ({
  path: "processing/new",
  element: <AddProcessing isWizard={isWizard} />,
  children: [
    {
      path: "step1/video",
      element: <AddProcessingDialogStep1 isWizard={isWizard} tab="video" />
    },
    {
      path: "step1/audio",
      element: <AddProcessingDialogStep1 isWizard={isWizard} tab="audio" />
    },
    {
      path: "step1/subtitles",
      element: <AddProcessingDialogStep1 isWizard={isWizard} tab="subtitles" />
    },
    {
      path: "step1/thumbnails",
      element: <AddProcessingDialogStep1 isWizard={isWizard} tab="thumbnails" />
    },
    {
      path: "step2",
      element: <AddProcessingDialogStep2 isWizard={isWizard} />
    }
  ]
})

const getEditProcessingRoute = () => ({
  path: "processing/:processingUuid",
  element: <EditProcessing />,
  children: [
    {
      path: "video",
      element: <EditProcessingTab tab="video" />
    },
    {
      path: "audio",
      element: <EditProcessingTab tab="audio" />
    },
    {
      path: "subtitles",
      element: <EditProcessingTab tab="subtitles" />
    },
    {
      path: "thumbnails",
      element: <EditProcessingTab tab="thumbnails" />
    },
    {
      path: "advanced",
      element: <EditProcessingTab tab="advanced" />
    }
  ]
})

const getEditOttEndpointRoute = () => ({
  path: "ottendpoints/:ottEndpointUuid",
  element: <Outlet />,
  children: [
    { path: "overview", element: <OttEndpointView tab="overview" /> },
    { path: "startover", element: <OttEndpointView tab="startover" /> }
  ]
})

export const playRoutes: Route<DefaultGenerics> = {
  path: "play",
  element: <App />,
  children: [
    {
      path: "pools",
      element: (
        <Main>
          <Pool />
        </Main>
      ),
      children: [
        {
          path: ":poolUuid",
          children: [
            { path: "/", element: <Status /> },
            { path: "analytics", element: <Analytics /> },
            { path: "assets", element: <AssetLibrary /> },
            {
              path: "live2vod",
              element: <Live2Vod />,
              children: [
                {
                  path: "recorders",
                  children: [
                    {
                      path: "/",
                      element: <RecorderList />
                    },
                    {
                      path: ":recorderUuid",
                      element: <RecorderView />,
                      children: [
                        {
                          path: "/",
                          element: <Navigate replace to="overview" />
                        },
                        {
                          path: "overview",
                          element: <RecorderOverviewTab />
                        },
                        {
                          path: "assets",
                          element: <RecorderAssetsTab />
                        },
                        {
                          path: "recordings",
                          element: <RecorderRecordingsTab />
                        }
                      ]
                    }
                  ]
                },
                {
                  path: "asset-collections",
                  children: [
                    {
                      path: "/",
                      element: <Navigate replace to="collections" />
                    },
                    {
                      path: "collections",
                      element: <AssetCollectionList />,
                      children: [
                        {
                          path: "/",
                          element: <AssetCollectionListView />
                        },
                        {
                          path: "trash",
                          element: <TrashList />
                        }
                      ]
                    },
                    {
                      path: ":assetCollectionUuid/endpoints/:endpointUuid",
                      element: <AssetCollectionEndpointView />
                    },
                    {
                      path: ":assetCollectionUuid",
                      element: <AssetCollectionView />,
                      children: [
                        {
                          path: "/",
                          element: <Navigate replace to="assets" />
                        },
                        {
                          path: "assets",
                          element: <AssetCollectionAssetList />
                        },
                        {
                          path: "endpoints",
                          element: <AssetCollectionEndpointList />
                        }
                      ]
                    }
                  ]
                }
              ]
            },

            { path: "catchup/programs/new", element: <AddProgram /> },
            { path: "catchup/programs/:programUuid/assets/new", element: <AddVodAsset /> },
            { path: "catchup/programs/:programUuid", element: <EditProgram /> },
            { path: "catchup/programs", element: <Programs /> },
            { path: "eventlog", element: <EventLog /> },
            {
              path: "settings",
              children: [
                { path: "/", element: <PoolSettingList /> },
                { path: "/cdns/:cdnUuid", element: <EditCdn /> },
                { path: "poolLocation", element: <PoolLocationForm /> },
                {
                  path: "apikeys",
                  children: [
                    { path: "new", element: <AddPoolApiKey /> },
                    { path: ":apiKeyUuid", element: <EditPoolApiKey /> }
                  ]
                }
              ]
            },
            {
              path: "live",
              children: [
                {
                  path: "inputs/:inputUuid",
                  element: <InputView />,
                  children: [
                    {
                      path: "overview",
                      element: <InputOverviewTab />
                    },
                    {
                      path: "live2vod",
                      element: <InputLive2VodTab />
                    },
                    {
                      path: "slate",
                      element: <InputSlateTab />
                    },
                    {
                      path: "fallback",
                      element: <InputFallbackTab />
                    },
                    {
                      path: "logo",
                      element: <InputLogoTab />
                    },
                    {
                      path: "configuration",
                      element: <InputConfigurationTab />,
                      children: [
                        {
                          path: ":streamUuid",
                          element: <InputStreamEdit />
                        }
                      ]
                    },
                    {
                      //virtual streams need streamUuid for API calls
                      path: ":streamUuid",
                      children: [
                        { path: "programs", element: <ProgramsTab /> },
                        { path: "fillers", element: <FillersTab /> },
                        { path: "logo", element: <InputLogoTab /> },
                        { path: "configuration", element: <VirtualConfigurationTab /> }
                      ]
                    }
                  ]
                },
                { path: "inputs", element: <InputList /> },
                getAddProcessingRoute(false),
                getEditProcessingRoute(),
                { path: "processing", element: <ProcessingList /> },
                getAddPackagingRoute(false),
                getEditPackagingRoute(),
                { path: "packaging", element: <PackagingList /> },
                {
                  path: "endpoints",
                  element: <EndpointsLayout />,
                  children: [
                    getEditOttEndpointRoute(),
                    { path: "ottendpoints", element: <OttEndpointList /> },
                    { path: "pushendpoints/:pushEndpointUuid", element: <ViewPushEndpoint /> },
                    { path: "pushendpoints", element: <PushEndpoints /> },
                    {
                      path: "l2vendpoints/:assetCollectionUuid/:endpointUuid",
                      element: <AssetCollectionEndpointView />
                    },
                    { path: "l2vendpoints", element: <L2vEndpointList /> }
                  ]
                }
              ]
            },
            { element: <NotFound /> }
          ]
        }
      ]
    },
    {
      path: "wizard/:poolUuid",
      element: <Wizard />,
      children: [
        { path: "properties", element: <WizardCloudSettings /> },
        {
          path: "input/:step",
          element: <AddInput />
        },
        getAddProcessingRoute(true),
        getAddPackagingRoute(true),
        getEditPackagingRoute(),
        { path: "packaging", element: <PackagingList isWizard /> },
        getEditOttEndpointRoute(),
        { path: "ottendpoints", element: <OttEndpointList isWizard /> },
        { path: "done", element: <WizardDone /> },
        {
          element: <NotFound />
        }
      ]
    },
    {
      path: "wizard",
      element: <Redirect to="new/properties" />
    },
    {
      path: "settings",
      element: (
        <Main>
          <Settings />
        </Main>
      ),
      children: [
        {
          path: "content-steering",
          children: [{ path: "/", element: <ContentSteeringServerForm /> }]
        },
        {
          path: "drm-providers",
          children: [
            { path: "/", element: <DRMProviderList /> },
            { path: ":drmProviderUuid", element: <EditDRMProvider /> }
          ]
        },
        {
          path: "admin-settings",
          children: [{ path: "/", element: <AdminSettings /> }]
        }
      ]
    },
    {
      element: <NotFound />
    }
  ]
}
